/**
 * "Download one by one" (BU-5, QA 2026-10-05): saves fired back to back (~16 ms apart) stop after the
 * 10th in Chrome — the rest never arrive, however long you wait. Measured in headless Chrome
 * (manual-qa/image-editor-p0/batch-model/dl-gap.js): 0 ms gap → 10 of 15; 150, 200, 300, 500, 1000 ms
 * → all 15, and all 50 at 150 and 300 ms. 300 ms keeps a margin and costs 15 s for a full batch of 50.
 */
export const DOWNLOAD_GAP_MS = 300;

/** R2-batch-edit-scope-and-handoff-i-6a (review 10): "one by one" knows only that the saves were handed to the browser,
 *  not that they arrived (a browser that blocks multiple downloads drops the rest) - so it says that, and the way out. */
export function eachDoneText(n: number): string {
    return n === 1 ? 'Sent 1 file to your browser.' : `Sent ${n} files to your browser. Not all there? Allow multiple downloads or use the ZIP.`;
}

export interface SaveEachOptions {
    gapMs?: number;
    wait?: (ms: number) => Promise<void>;
    cancelled?: () => boolean;
    onProgress?: (done: number, total: number) => void;
}

const extOf = (n: string) => { const m = /\.([^.\\/]+)$/.exec(n || ''); return m ? m[1].toLowerCase() : ''; };

/**
 * Names that tell the files apart, the same in the ZIP and one by one (PLAN C3; fix-all 2026-10-06 formats-13,
 * power-10 — "one by one" used to hand the browser two identical names and let it pick "(1)" or overwrite).
 * When outputs clash and their `sources` are of different formats, the source format goes into the name
 * (alpha.tiff + alpha.heic → alpha-tiff-400x300.jpg, alpha-heic-400x300.jpg); any clash left gets -2, -3 …
 * on the later files, never a name another file already has. Case-insensitive (Windows / macOS unzip).
 */
export function uniqueNames(names: string[], sources?: (string | undefined)[]): string[] {
    const key = (n: string) => n.toLowerCase();
    let out = names.slice();
    if (sources) {
        const groups = new Map<string, number[]>();
        out.forEach((n, i) => { const g = groups.get(key(n)) || []; g.push(i); groups.set(key(n), g); });
        groups.forEach(idx => {
            if (idx.length < 2) return;
            const exts = idx.map(i => extOf(sources[i] || ''));
            if (exts.some(e => !e) || new Set(exts).size !== exts.length) return;   // only when the format tells them apart
            idx.forEach((i, j) => { out[i] = out[i].replace(/(-\d+x\d+)?(\.[^.]+)?$/, m => `-${exts[j]}${m}`); });
        });
    }
    const taken = new Set<string>(); const firsts = new Set<number>();
    out.forEach((n, i) => { if (!taken.has(key(n))) { taken.add(key(n)); firsts.add(i); } });
    out = out.map((n, i) => {
        if (firsts.has(i)) return n;
        let k = 1, c = n;
        do { c = n.replace(/(\.[^.]+)?$/, m => `-${++k}${m}`); } while (taken.has(key(c)));
        taken.add(key(c)); return c;
    });
    return out;
}

/** Saves each file in order with a gap between them; returns how many were handed to the browser.
 *  Names go through uniqueNames (`source` = the original file name, optional). */
export async function saveSequentially(input: { name: string; blob: Blob; source?: string }[], save: (blob: Blob, name: string) => void, opts: SaveEachOptions = {}): Promise<number> {
    const names = uniqueNames(input.map(f => f.name), input.map(f => f.source));
    const files = input.map((f, i) => ({ blob: f.blob, name: names[i] }));
    const gap = Math.max(DOWNLOAD_GAP_MS, opts.gapMs ?? DOWNLOAD_GAP_MS);
    const wait = opts.wait ?? ((ms: number) => new Promise<void>(r => setTimeout(r, ms)));
    let saved = 0;
    for (let i = 0; i < files.length; i++) {
        if (opts.cancelled?.()) break;
        if (i > 0) await wait(gap);
        if (opts.cancelled?.()) break;
        try { save(files[i].blob, files[i].name); saved++; opts.onProgress?.(saved, files.length); }
        catch { /* one file the browser refused never stops the others */ }
    }
    return saved;
}
